import React, { useCallback, useState } from 'react'
import styles from 'components/answers/thin-answer.module.scss'
import { UserStream } from 'components/filters/FilterIcon'
import SpaceIcon from 'components/spaces/SpaceIcon'
import answerModel from 'components/answers/answerModel'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import { AnswerImage } from 'components/answers/Answer'
import { Stream } from 'components/quests/ThinQuest'
import cn from 'classnames'
import CopyLinkButton from 'components/shared/CopyLinkButton'
import StarQuestButton from 'components/quests/StarQuestButton'
import questModel from 'components/quests/questModel'
import { getAnswerExternalShareUrl } from 'utils/shareUtils'
import api from 'api/api'
import { isUserMuted } from 'utils/muteUtils'
import MutedContentPlaceholder from 'components/shared/MutedContentPlaceholder'
import UnknownUserIcon from 'public/unknown-user-icon.png'
import SatsIcon from 'components/shared/SatsIcon'
import { format as formatCompactSats } from 'lib/bsv-util'
import { streamReplyCardsEnabled } from 'lib/streamCards'
import { answerIsNewSinceQuestVisit } from 'lib/questStreamRenderedAnswers'
import type { Answer } from 'types/answers'
import type { Quest } from 'types/quests'

type ThinAnswerQuestRef = Omit<Partial<Quest>, 'id'> & {
    id?: string | null
}

type ThinAnswerData = Omit<Partial<Answer>, 'child_quests'> & {
    id?: string | null
    child_quests?: ThinAnswerQuestRef[]
}

export default function ThinAnswer({
    answer,
    quest,
    onClick: propsOnClick,
    contextStyles,
    showStream = false,
    showControls = false,
    isLast = false,
    isFirst = false,
    streamCardSignals = false,
}: {
    answer: ThinAnswerData
    quest?: ThinAnswerQuestRef
    onClick?: () => void
    contextStyles?: string
    showStream?: boolean
    showControls?: boolean
    isLast?: boolean
    isFirst?: boolean
    // Stream-card row signals (docs/stream-cards-architecture.md): the unseen dot in the
    // left gutter + the earned-upvalue amount on the right margin. Opt-in per surface so
    // sidebar/child-quest thin rows keep their stock look.
    streamCardSignals?: boolean
}) {
    const [isHovered, setIsHovered] = useState(false)
    const user = answer?.user
    const team = quest?.team
    const filter = quest ? questModel.getFilter(quest) : null

    const isSystemUser = useCallback(() => {
        return answer?.is_system || answer?.user?.system || answer?.user_id === gon.SYSTEM_USER
    }, [answer])

    // Notification backing posts live in is_system quests and render as a
    // "quoted" stub. System posts in ordinary public threads (Treechat Radio)
    // are real content and render like anyone else's. Unknown quest keeps the
    // stub, the historical default.
    const isNotiSystemPost = isSystemUser() && (quest?.is_system ?? true)

    const isAgent = useCallback(() => {
        return answer?.message_type === 'chatgpt' || answer?.message_type === 'agent'
    }, [answer])

    const onClick = useCallback(
        ev => {
            ev.preventDefault()
            ev.stopPropagation()

            // Track thin answer click
            window.logEv('CLICK_THIN_ANSWER', {
                answer_id: answer?.id,
                quest_id: answer?.quest_id || quest?.id,
                from_location: 'thin_answer',
                has_image: !!answer?.answer_image_url,
                has_url: !!answer?.url,
            })

            if (propsOnClick) {
                propsOnClick()
            }
        },
        [answer?.id, answer?.quest_id],
    )

    // Check if user is muted (but not if it's a system user)
    const isMuted = !isSystemUser() && answer?.user?.id && isUserMuted(String(answer.user.id))

    const rowSignalsEnabled = streamCardSignals && streamReplyCardsEnabled()
    const upvalueSats = Number(answer?.bsv_upvalue_amt || 0)
    const showUpvalueAmount = rowSignalsEnabled && upvalueSats > 0 && !isSystemUser() && !isMuted
    const isNewSinceVisit = rowSignalsEnabled && answerIsNewSinceQuestVisit(answer, quest)

    return (
        <div
            className={cn(
                styles.thinAnswerComp,
                contextStyles,
                isHovered && styles.hovered,
                isLast && styles.lastItem,
            )}
            data-answer-id={answer?.id}
            data-click-intent="open-thread-answer"
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {isNewSinceVisit && (
                <span className={styles.unseenDot} data-testid="thin-answer-unseen-dot" />
            )}

            {showStream && team && (
                <div className={styles.streamIconContainer}>
                    <Stream team={team} user={user} filter={filter} />
                </div>
            )}

            <div className={styles.userStreamContainer}>
                {isNotiSystemPost ? (
                    <div className={styles.privateIcon}>
                        <SpaceIcon contextStyles={styles.spaceIcon} />
                    </div>
                ) : isMuted ? (
                    <div className={cn(styles.userIcon)}>
                        <img src={UnknownUserIcon} alt="Muted user" />
                    </div>
                ) : (
                    <UserStream
                        user={user}
                        contextStyles={cn(styles.userIcon, isAgent() && styles.agentIcon)}
                        agentIcon={isAgent()}
                        // @ts-expect-error FilterIcon JSDoc says boolean but UserIcon uses it as a URL string
                        virtualUserIcon={answer?.virtual_user_icon_url}
                        size={'small'}
                    />
                )}
            </div>

            <div className={styles.contentContainer}>
                {isNotiSystemPost ? (
                    <div className={styles.privateAnswer}>
                        <div className={styles.privateLabel}>This message was quoted.</div>
                    </div>
                ) : isMuted ? (
                    <div className={styles.mutedContent}>
                        <span className={styles.mutedText}>Content from a muted user</span>
                    </div>
                ) : (
                    <ThinAnswerContent answer={answer} isQuestDraft={false} />
                )}
            </div>

            {showUpvalueAmount && (
                <div
                    className={styles.upvalueAmount}
                    data-testid="thin-answer-upvalue-amount"
                    title={`${Math.round(upvalueSats).toLocaleString()} sats of upvalue`}
                >
                    <SatsIcon contextStyles={styles.upvalueAmountIcon} />
                    <span>{formatCompactSats(Math.round(upvalueSats))}</span>
                </div>
            )}

            {showControls && (
                <>
                    <div className={cn(styles.stackButton)}>
                        <CopyLinkButton
                            showLabel={false}
                            tooltip
                            getPublicLink={async ev => {
                                const fallbackUrl = getAnswerExternalShareUrl(answer)
                                if (!gon?.currentUser || !answer?.id) return fallbackUrl

                                try {
                                    const result = await api.getAnonymousShareLink(answer.id)
                                    return result?.url || fallbackUrl
                                } catch {
                                    return fallbackUrl
                                }
                            }}
                        />
                    </div>

                    <div className={cn(styles.stackButton)}>
                        <StarQuestButton
                            quest={answer?.child_quests?.[0]}
                            contextStyles={styles.starQuestButton}
                        />
                    </div>
                </>
            )}
        </div>
    )
}

export function ThinAnswerContent({ answer, isQuestDraft }) {
    const text = answer ? answerModel.getText(answer) : ''
    const url = (!answer?.url?.tweet_id && answer?.url?.title) || answer?.url?.address
    const empty = !isQuestDraft && !text && !url

    return (
        <ErrorBoundary label={'ThinAnswerContent'}>
            <div className={cn(styles.thinAnswerContentComp, styles.questName)}>
                {isQuestDraft && <div className={cn(styles.label, styles.draftLabel)}>DRAFT</div>}

                {answer?.answer_image_url && (
                    <AnswerImage
                        answer={answer}
                        contextStyles={styles.image}
                        imageSize="small"
                        isThin={true}
                    />
                )}

                {text && <div className={cn(styles.text, styles.ellipsis)}>{text}</div>}

                {url && (
                    <>
                        <div className={styles.label}>CLIP</div>
                        <div className={cn(styles.url, styles.ellipsis)}>{url}</div>
                    </>
                )}

                {empty && (
                    <div
                        className={cn(styles.empty, styles.ellipsis)}
                    >{`message-${answer?.id}`}</div>
                )}
            </div>
        </ErrorBoundary>
    )
}
